import { useEffect, useCallback, useState, useRef } from 'react';
import useSWR from 'swr';
import Cookies from 'js-cookie';
import { useFlagship } from '@flagship.io/react-sdk';
import { useRequestQueue } from '@web/queue/context';
import { ApiResult, useService } from '../api';
import { useCampaign } from '../campaign';
import { useExperiment } from '../experiments/useExperiment';
import {
  CHECKOUT_NEXT_BOX_MVP,
  CHECKOUT_GWP_CALLOUT,
} from '../experiments/constants';
import { ShopifyCartService } from './services';

export const useShopifyCart = ({
  shouldCreateCart = true,
  hasCheckedPath = false,
} = {}) => {
  const service = useService(ShopifyCartService);
  const { queueRequest } = useRequestQueue();
  const { campaignId } = useCampaign();
  const { visitor } = useFlagship();
  const [cartId, setCartId] = useState(null);
  const creatingRef = useRef(null);
  const cartIdRef = useRef(cartId);
  const { value: showCheckoutNextBoxMVP } = useExperiment(
    CHECKOUT_NEXT_BOX_MVP,
    false,
  );
  const { value: showCheckoutGWPCallout } = useExperiment(
    CHECKOUT_GWP_CALLOUT,
    false,
  );

  // Keep cartIdRef.current updated with the latest cartId
  cartIdRef.current = cartId;

  const updateCartId = useCallback(
    newId => {
      if (newId && newId !== cartId) {
        setCartId(newId);
        Cookies.set('shopifyCartId', newId, { path: '/', expires: 30 });
      }
    },
    [cartId],
  );

  const applyStoredOfferCode = useCallback(
    async newCartId => {
      // The offer code in localStorage is persistent for attribution: it is
      // written whenever a user lands with ?offer_code=... (in either the
      // app-router or pages-router useQueryOfferDetails) and is intentionally
      // NOT cleared after being applied. The latest captured code always wins
      // because the write call overwrites on every new URL hit. This call
      // applies it to any freshly-created cart so the coupon survives the
      // app-router → pages-router boundary.
      if (typeof window === 'undefined') return;
      let storedOfferCode = null;
      try {
        storedOfferCode = window.localStorage.getItem('offerCode');
      } catch (e) {
        return;
      }
      if (!storedOfferCode) return;
      try {
        await service.addPromo(newCartId, storedOfferCode);
      } catch (e) {
        // Non-fatal — if the code is invalid the cart just starts with the
        // default offer and the pages-router override logic takes it from
        // there.
      }
    },
    [service],
  );

  const createCart = useCallback(async () => {
    if (cartIdRef.current) return { cart_id: cartIdRef.current };

    //* If a creation is already in progress, return the existing promise
    if (creatingRef.current) {
      return creatingRef.current;
    }

    creatingRef.current = (async () => {
      try {
        const response = await service.createCart();

        if (response?.data?.cart_id) {
          // Apply the stored offer code BEFORE setting cartId. Otherwise
          // updateCartId triggers the SWR fetch immediately and the cart
          // data lands in state without the coupon, which causes the
          // pages-router ShopifyOfferProvider to then apply DEFAULT over
          // the top. Applying first means the SWR fetch sees the cart
          // already couponed.
          await applyStoredOfferCode(response.data.cart_id);
          updateCartId(response.data.cart_id);
          return response.data;
        } else {
          return response?.data || null;
        }
      } catch (error) {
        creatingRef.current = null;
        return null;
      }
    })();

    return creatingRef.current;
  }, [service, updateCartId, applyStoredOfferCode]);

  const {
    data: cartData,
    error: cartError,
    isLoading: isShopifyCartLoading,
    mutate: refreshCart,
  } = useSWR(cartId ? `/api/v4/onboarding/${cartId}/` : null, null, {
    revalidateOnFocus: false,
    dedupingInterval: 10000,
    onSuccess: async data => {
      const flags = {
        showCheckoutNextBoxMVP,
        showCheckoutGWPCallout,
      };

      const abTestFlags = Object.entries(flags)
        .map(([key, value]) => `${key}:${value}`)
        .join(',');

      const attributes = {
        order_type: 'onboarding',
        cart_id: data.cart_id,
        ab_tests: abTestFlags,
      };

      queueRequest(() => updateCartAttributes(attributes, data.cart_id));
    },
    onError: async error => {
      if (error.response?.status === 404) {
        Cookies.remove('shopifyCartId', { path: '/' });
        setCartId(null);
        if (shouldCreateCart) {
          await createCart();
        }
      }
    },
    onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
      if (retryCount >= 1) return;
      setTimeout(() => revalidate({ retryCount }), 1000);
    },
  });

  const syncRecart = useCallback(
    id => {
      const sessionId = window._recart?.getSessionId?.();
      if (!sessionId || !id) return;
      service.syncRecartCart(sessionId, id).catch(err =>
        // eslint-disable-next-line no-console
        console.warn('Recart sync failed:', err),
      );
    },
    [service],
  );

  const addPetData = useCallback(
    async (petData = {}) => {
      let currentCartId = cartIdRef.current; // potential race condition handling with ref

      // If no cartId, try to create one
      if (!currentCartId) {
        const newCart = await createCart();
        currentCartId = newCart?.cart_id;
      }

      if (!currentCartId) {
        return null;
      }

      try {
        const response = await service.addPetData(currentCartId, {
          ...petData,
        });

        if (!currentCartId) {
          return null;
        }

        await refreshCart(response.data, false);
        return response?.data;
      } catch (error) {
        throw error;
      }
    },
    [service, refreshCart, createCart],
  );

  const deletePetFromCart = useCallback(
    async petId => {
      if (!cartId) return null;

      const response = await service.removePet(cartId, petId);
      await refreshCart(response.data, false);
      return response.data;
    },
    [service, cartId, refreshCart],
  );

  const addPromo = useCallback(
    async promoCode => {
      if (!cartId || cartError) return null;

      const response = await service.addPromo(cartId, promoCode);
      await refreshCart(response.data, false);
      return response.data;
    },
    [service, cartId, cartError, refreshCart],
  );

  const upsertMealPlan = useCallback(
    async (petId, planData = {}) => {
      if (!cartId) return null;
      return ApiResult.callAsync(async () => {
        const response = await service.upsertMealPlan(cartId, petId, planData);
        await refreshCart(response.data, false);
        syncRecart(cartId);
        return response.data;
      });
    },
    [service, cartId, refreshCart, syncRecart],
  );

  const attachExtras = useCallback(
    async (petId, extrasData = []) => {
      if (!cartId || !petId) return null;
      return ApiResult.callAsync(async () => {
        const response = await service.attachExtras(cartId, petId, extrasData);
        refreshCart(response.data, false);
        syncRecart(cartId);
        return response.data;
      });
    },
    [service, cartId, refreshCart, syncRecart],
  );

  const attachFreeGifts = useCallback(
    async (petId, giftData = {}) => {
      if (!cartId || !petId) return null;
      return ApiResult.callAsync(async () => {
        const response = await service.attachFreeGifts(cartId, petId, giftData);
        refreshCart(response.data, false);
        return response.data;
      });
    },
    [service, cartId, refreshCart],
  );

  const updateCartAttributes = useCallback(
    async (attributes = {}, idOverride = null) => {
      const id = idOverride || cartId;

      if (!id) {
        // eslint-disable-next-line no-console
        console.warn('updateCartAttributes: No Cart ID available');
        return null;
      }

      return ApiResult.callAsync(async () => {
        const response = await service.updateAttributes(id, attributes);
        await refreshCart(response.data, false);
        return response.data;
      });
    },
    [service, cartId, refreshCart],
  );

  useEffect(() => {
    //* I found that the createCart call being awaited was better for adding the attribute following.
    const initCart = async () => {
      const savedCartId = Cookies.get('shopifyCartId');

      if (savedCartId) {
        setCartId(savedCartId);
      } else if (shouldCreateCart) {
        try {
          await createCart();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to initialize cart:', error);
        }
      }
    };

    initCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldCreateCart]);

  //* Update cart attributes with campaign_id & cart_id when cart is first created
  useEffect(() => {
    if (!cartId || !campaignId) return;
    const updateCampaign = async campaignId => {
      queueRequest(() =>
        updateCartAttributes({
          campaign_id: campaignId,
        }),
      );
    };
    updateCampaign(campaignId);
  }, [cartId, queueRequest, updateCartAttributes, campaignId]);

  const getCouponCode = useCallback(() => {
    if (!cartData?.coupon_code?.length) return null;
    return cartData.coupon_code[0];
  }, [cartData]);

  // Sync the offerCode cookie, localStorage, and Flagship visitor context with
  // the cart's current coupon code whenever it changes.
  useEffect(() => {
    if (!cartData || !cartId) return;

    const cartCouponCode = cartData?.coupon_code?.[0] ?? '';
    const cookieOfferCode = Cookies.get('offerCode') ?? '';

    if (cartCouponCode === cookieOfferCode) return;

    if (cartCouponCode) {
      Cookies.set('offerCode', cartCouponCode, { path: '/' });
      try {
        window.localStorage.setItem('offerCode', cartCouponCode);
      } catch (e) {}
    } else {
      Cookies.remove('offerCode', { path: '/' });
      try {
        window.localStorage.removeItem('offerCode');
      } catch (e) {}
    }

    if (visitor) {
      visitor.updateContext({ offerCode: cartCouponCode?.toLowerCase() });
      visitor.fetchFlags();
    }
  }, [cartData, cartId, visitor]);

  const isLoading =
    !hasCheckedPath ||
    isShopifyCartLoading ||
    (shouldCreateCart && !cartData && !cartError);

  return {
    cartId,
    cart: cartData || {},
    error: cartError,
    isLoading,
    createCart,
    addPetData,
    deletePetFromCart,
    addPromo,
    upsertMealPlan,
    attachExtras,
    attachFreeGifts,
    refreshCart,
    updateCartAttributes,
    couponCode: getCouponCode(),
  };
};
